import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme } from 'antd';
import type { Icon } from '@phosphor-icons/react';
import {
    ArrowsLeftRightIcon,
    CaretCircleLeftIcon,
    CaretCircleRightIcon,
    ClipboardTextIcon,
    ConfettiIcon,
    FolderOpenIcon,
    KeyIcon,
    ListIcon,
    ListNumbersIcon,
    PackageIcon,
    QuestionMarkIcon,
    SquaresFourIcon,
    UsersIcon,
    WalletIcon,
} from '@phosphor-icons/react';
import AccountHeader from './AccountHeader';
import { useTheme } from 'components/provider/theme';
import styles from './layout.module.css';

type NavItem = {
    key: string;
    path: string;
    label: string;
    icon: Icon;
};

const NAV_ITEMS: NavItem[] = [
    { key: '1',  path: '/',              label: 'Dashboard',       icon: SquaresFourIcon },
    { key: '2',  path: '/users',         label: 'User Management', icon: UsersIcon },
    { key: '3',  path: '/accounts',      label: 'Trading Accounts',icon: WalletIcon },
    { key: '4',  path: '/transactions',  label: 'Transactions',    icon: ArrowsLeftRightIcon },
    { key: '5',  path: '/role',          label: 'Roles',           icon: KeyIcon },
    { key: '6',  path: '/permissions',   label: 'Permissions',     icon: KeyIcon },
    // { key: '7',  path: '/risk-management',    label: 'Risk Management',    icon: PackageIcon },
    // { key: '8',  path: '/reports',            label: 'Reports',            icon: PackageIcon },
    { key: '9',  path: '/events',        label: 'Events',          icon: ConfettiIcon },
    // { key: '10', path: '/collaborators',      label: 'Collaborators',      icon: PackageIcon },
    // { key: '11', path: '/daily',              label: 'Daily',              icon: PackageIcon },
    { key: '12', path: '/numerology',    label: 'Numerology',      icon: ListNumbersIcon },
    { key: '13', path: '/product',       label: 'Product',         icon: PackageIcon },
    { key: '14', path: '/stores',        label: 'Stores',          icon: PackageIcon },
    { key: '15', path: '/subscriptions', label: 'Subscriptions',   icon: ClipboardTextIcon },
    { key: '16', path: '/point',         label: 'Point',           icon: ListIcon },
    { key: '17', path: '/bots',          label: 'Bots',            icon: PackageIcon },
    { key: '18', path: '/recomendation', label: 'Recomendation',   icon: PackageIcon },
    // { key: '19', path: '/coach-daily',        label: 'Coach Daily',        icon: PackageIcon },
    { key: '23', path: '/library',       label: 'Library',         icon: FolderOpenIcon },
    { key: '20', path: '/help',          label: 'Help',            icon: QuestionMarkIcon },
    // { key: '21', path: '/product-section',    label: 'Product Section',    icon: PackageIcon },
    // { key: '22', path: '/product-introduction', label: 'Product Introduction', icon: PackageIcon },
];

interface SidebarLayoutProps {
    children: React.ReactNode;
    title?: React.ReactNode;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children, title }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [pinned, setPinned] = useState(() => localStorage.getItem('sidebarPinned') === 'true');
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';

    const selectedKey = useMemo(() => {
        const exact = NAV_ITEMS.find(item => item.path === pathname);
        if (exact) return exact.key;
        const prefix = NAV_ITEMS
            .filter(item => item.path !== '/' && pathname.startsWith(item.path + '/'))
            .sort((a, b) => b.path.length - a.path.length)[0];
        return prefix?.key ?? '1';
    }, [pathname]);

    const pageTitle = useMemo(() => {
        const exact = NAV_ITEMS.find(item => item.path === pathname);
        if (exact) return exact.label;
        const prefix = NAV_ITEMS
            .filter(item => item.path !== '/' && pathname.startsWith(item.path))
            .sort((a, b) => b.path.length - a.path.length)[0];
        return prefix?.label ?? 'Admin Dashboard';
    }, [pathname]);

    useEffect(() => {
        document.title = `Lumir | ${pageTitle}`;
    }, [pageTitle]);

    const togglePin = () => {
        const next = !pinned;
        setPinned(next);
        localStorage.setItem('sidebarPinned', String(next));
    };

    useEffect(() => { setMobileOpen(false); }, [pathname]);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    return (
        <ConfigProvider
            getPopupContainer={() => document.body}
            select={{ styles: { popup: { root: { zIndex: 99999 } } } }}
            theme={{
                algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: {
                    colorPrimary: '#1677ff',
                    colorBorder: isDark ? '#4b5563' : '#d1d5db',
                    borderRadius: 6,
                    borderRadiusLG: 8,
                    fontSize: 14,
                    controlHeight: 32,
                    zIndexPopupBase: 9999,
                },
                components: {
                    Input: {
                        colorBgContainer: isDark ? '#1e2939' : undefined,
                        colorBorder: isDark ? '#4b5563' : '#d1d5db',
                        colorText: isDark ? '#fff' : undefined,
                        colorTextPlaceholder: isDark ? 'rgba(255,255,255,0.4)' : undefined,
                    },
                    Select: {
                        colorText: isDark ? '#fff' : '#000',
                        colorBgContainer: isDark ? '#1e2939' : '#fff',
                        colorBgElevated: isDark ? '#374151' : '#fff',
                        colorTextPlaceholder: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                    },
                    DatePicker: {
                        colorText: isDark ? '#fff' : undefined,
                        colorTextPlaceholder: isDark ? 'rgba(255,255,255,0.4)' : undefined,
                        colorBgContainer: isDark ? '#1e2939' : undefined,
                        colorBgElevated: isDark ? '#374151' : undefined,
                        colorIcon: isDark ? '#fff' : undefined,
                    },
                    Table: {
                        borderColor: isDark ? '#374151' : '#e5e7eb',
                        headerBg: isDark ? '#111827' : '#fafafa',
                        colorBgContainer: isDark ? '#0e192b' : '#ffffff',
                        colorText: isDark ? '#f9fafb' : '#111827',
                        rowHoverBg: isDark ? '#1f2937' : '#f5f5f5',
                        headerColor: isDark ? '#9ca3af' : '#374151',
                    },
                    Modal: {
                        contentBg: isDark ? '#1f2937' : '#fff',
                        headerBg: isDark ? '#1f2937' : '#fff',
                        colorText: isDark ? '#f9fafb' : '#111827',
                        titleColor: isDark ? '#f9fafb' : '#111827',
                    },
                    Pagination: {
                        colorText: isDark ? '#fff' : undefined,
                        colorBgTextHover: isDark ? '#374151' : undefined,
                    },
                    Breadcrumb: {
                        itemColor: isDark ? '#9ca3af' : '#6b7280',
                        lastItemColor: isDark ? '#f9fafb' : '#111827',
                        separatorColor: isDark ? '#4b5563' : '#d1d5db',
                    },
                    Divider: { colorSplit: isDark ? '#374151' : '#e5e7eb' },
                    Empty: { colorTextDescription: isDark ? '#6b7280' : '#9ca3af' },
                },
            }}
        >
            <div className={styles.layout}>
                {mobileOpen && (
                    <div className={styles.overlay} onClick={() => setMobileOpen(false)} />
                )}

                {/* sidebar-light class mirrors app light mode */}
                <aside className={[
                    styles.sidebar,
                    mobileOpen ? styles.sidebarOpen : '',
                    pinned ? styles.sidebarPinned : '',
                    !isDark ? 'sidebar-light' : '',
                ].filter(Boolean).join(' ')}>
                    <div className={styles.sidebarLogo}>
                        <img src="/images/favicon.png" alt="BeQ" className={styles.logoIcon} />
                        <span className={styles.logoText}>BeQ Holdings</span>
                        <button
                            className={`${styles.pinBtn} ${pinned ? styles.pinBtnActive : ''}`}
                            onClick={togglePin}
                            title={pinned ? 'Thu gọn sidebar' : 'Ghim sidebar'}
                        >
                            {pinned
                                ? <CaretCircleLeftIcon size={18} weight="fill" />
                                : <CaretCircleRightIcon size={18} weight="fill" />
                            }
                        </button>
                    </div>

                    <nav className={styles.sidebarNav}>
                        {NAV_ITEMS.map(({ key, path, label, icon: NavIcon }) => (
                            <div
                                key={key}
                                className={`${styles.navItem} ${key === selectedKey ? styles.navItemActive : ''}`}
                                onClick={() => navigate(path)}
                            >
                                <span className={styles.navIcon}><NavIcon size={18} /></span>
                                <span className={styles.navLabel}>{label}</span>
                            </div>
                        ))}
                    </nav>
                </aside>

                <main className={styles.main}>
                    <header className={styles.header}>
                        <button
                            className={styles.menuBtn}
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open menu"
                        >
                            <ListIcon size={20} />
                        </button>
                        <span className={styles.headerTitle}>{title ?? pageTitle}</span>
                        <AccountHeader isDark={isDark} onToggleTheme={toggleTheme} />
                    </header>

                    <div className={styles.content}>{children}</div>

                    <footer className={styles.footer}>
                        BeqHolding {new Date().getFullYear()} © Created by BeqHolding Team
                    </footer>
                </main>
            </div>
        </ConfigProvider>
    );
};

export default SidebarLayout;
